import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useActive } from "../active/active.functions";
import { PastContext } from "../past/past.functions";
import { pickChatColor, getProfileSummaries } from "../../api/profile";
import {
  getLatestIncomingSkFromMessages,
  invalidateCommunicationInboxSnapshot,
  persistConversationReadMarker,
} from "../../api/communication";
import { toast } from "../../components/toast";

const ALLOWED_FONT_SIZES = new Set(["small", "medium", "large"]);

function normalize(value) {
  return String(value ?? "").trim().toLowerCase();
}

function statusPriority(chat) {
  const status = String(chat?.status ?? "accepted");
  if (status === "accepted") return 3;
  if (status === "pending" && chat?.isInitiatedByMe) return 2;
  if (status === "pending") return 1;
  return 0;
}

function hexToRgb(hexColor) {
  const hex = String(hexColor || "").trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  const toHex = (value) => {
    const safe = Math.min(255, Math.max(0, Math.round(value)));
    return safe.toString(16).padStart(2, "0");
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Normalizes persisted chat font-size preferences before applying them to the UI.
export function normalizeChatFontSize(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return ALLOWED_FONT_SIZES.has(normalized) ? normalized : "medium";
}

// Formats message timestamps from persisted chat rows without ever rendering "Invalid Date".
// Uses Intl.DateTimeFormat to stay consistent with formatConversationTimestamp.
const MESSAGE_TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
  hour: "numeric",
  minute: "2-digit",
});

function formatDateAsMessageTime(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return MESSAGE_TIME_FORMATTER.format(date);
}

export function formatMessageTime(raw) {
  if (!raw) return "";

  if (typeof raw === "string" && raw.includes("#")) {
    const [epochPart] = raw.split("#");
    const epoch = Number(epochPart);
    if (!Number.isNaN(epoch)) {
      return formatDateAsMessageTime(new Date(epoch));
    }
    return "";
  }

  if (typeof raw === "number") {
    return formatDateAsMessageTime(new Date(raw));
  }

  if (typeof raw === "string") {
    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) {
      return formatDateAsMessageTime(date);
    }
    return raw;
  }

  return "";
}

// Picks a readable foreground color for message bubbles derived from profile chat colors.
export function getReadableTextColor(hexColor) {
  const hex = String(hexColor || "").replace("#", "");
  if (hex.length !== 6) return "#ffffff";
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 145 ? "#111827" : "#ffffff";
}

// Softens the counterpart bubble color so incoming messages stay readable against the thread background.
export function tintWithWhite(hexColor, amount = 0.82) {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return "#f3f4f6";
  const mix = Math.min(1, Math.max(0, Number(amount)));
  return rgbToHex({
    r: rgb.r + (255 - rgb.r) * mix,
    g: rgb.g + (255 - rgb.g) * mix,
    b: rgb.b + (255 - rgb.b) * mix,
  });
}

// Calculates profile age for the chat profile modal when a counterpart has a date of birth.
export function calculateChatProfileAge(dateOfBirth, today = new Date()) {
  if (!dateOfBirth) return null;
  const birthDate = new Date(dateOfBirth);
  if (Number.isNaN(birthDate.getTime())) return null;

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  return age;
}

function useEnsureSelectedChat() {
  const location = useLocation();
  const navigate = useNavigate();
  const { username: usernameParam } = useParams();
  const isPastChat = location.pathname.startsWith("/past/chat/");

  const activeContext = useActive();
  const pastContext = useContext(PastContext);

  const {
    chatListLoading,
    availableChats,
    selectedChatPk,
    setSelectedChatPk,
    currentChat,
  } = isPastChat ? pastContext : activeContext;

  const pastChat = location?.state?.chat;
  const missingToastedRef = useRef("");

  const username = useMemo(() => {
    try {
      return decodeURIComponent(usernameParam || "");
    } catch {
      return usernameParam || "";
    }
  }, [usernameParam]);

  const stateSelectedPk = location?.state?.selectedChatPk || null;

  useEffect(() => {
    const hasChats = Array.isArray(availableChats) && availableChats.length > 0;
    const statePkExists =
      !!stateSelectedPk && availableChats?.some((chat) => chat.chatPk === stateSelectedPk);
    const currentPkExists =
      !!selectedChatPk && availableChats?.some((chat) => chat.chatPk === selectedChatPk);

    if (statePkExists && stateSelectedPk !== selectedChatPk) {
      setSelectedChatPk(stateSelectedPk);
      return;
    }

    if (stateSelectedPk && !statePkExists && hasChats) {
      const matches = availableChats.filter(
        (chat) => normalize(chat.title || chat.name) === normalize(username)
      );
      matches.sort((a, b) => {
        const byStatus = statusPriority(b) - statusPriority(a);
        if (byStatus !== 0) return byStatus;
        return String(b?.lastUpdatedAt ?? "").localeCompare(String(a?.lastUpdatedAt ?? ""));
      });
      const fallback = matches[0];
      if (fallback && fallback.chatPk !== selectedChatPk) {
        setSelectedChatPk(fallback.chatPk);
      }
      return;
    }

    if (!stateSelectedPk && username && hasChats) {
      const matches = availableChats.filter(
        (chat) => normalize(chat.title || chat.name) === normalize(username)
      );
      matches.sort((a, b) => {
        const byStatus = statusPriority(b) - statusPriority(a);
        if (byStatus !== 0) return byStatus;
        return String(b?.lastUpdatedAt ?? "").localeCompare(String(a?.lastUpdatedAt ?? ""));
      });
      const match = matches[0];
      if (match && match.chatPk !== selectedChatPk) {
        setSelectedChatPk(match.chatPk);
        return;
      }
    }

    // The chat list has finished loading and the URL points at a username
    // that doesn't match anything we can see. The previous behavior fell
    // back to availableChats[0] (the most recent chat), which silently
    // hijacked the URL. Surface the missing chat instead so the user knows
    // the conversation is gone (block, account deletion, manual delete).
    const isUsernameTargetedRoute =
      Boolean(username) && !stateSelectedPk && !currentPkExists;
    if (
      isUsernameTargetedRoute &&
      !chatListLoading &&
      missingToastedRef.current !== username
    ) {
      missingToastedRef.current = username;
      toast.info("This chat no longer exists.");
      navigate(isPastChat ? "/past" : "/active", { replace: true });
      return;
    }

    if (hasChats && !currentPkExists && !username) {
      setSelectedChatPk(availableChats[0].chatPk);
    }
  }, [
    availableChats,
    chatListLoading,
    isPastChat,
    navigate,
    selectedChatPk,
    setSelectedChatPk,
    stateSelectedPk,
    username,
  ]);

  const headerName = pastChat?.name || currentChat?.title || currentChat?.name || username || "Chat";

  return { headerName, isPastChat, pastChat };
}

// Treat a scroller as "near the bottom" when the remaining scroll distance
// is below this many pixels. We don't hijack scroll position when the user
// has scrolled up to read history.
export const NEAR_BOTTOM_THRESHOLD_PX = 100;

export function isScrollerNearBottom(element, thresholdPx = NEAR_BOTTOM_THRESHOLD_PX) {
  if (!element) return false;
  const distanceFromBottom =
    Number(element.scrollHeight || 0) -
    Number(element.scrollTop || 0) -
    Number(element.clientHeight || 0);
  return distanceFromBottom <= thresholdPx;
}

function useAutoScrollToBottom(messages) {
  const scrollerRef = useRef(null);
  // Default to true so the very first render scrolls to the latest message.
  const wasNearBottomRef = useRef(true);

  useEffect(() => {
    const element = scrollerRef.current;
    if (!element) return undefined;
    const handleScroll = () => {
      wasNearBottomRef.current = isScrollerNearBottom(element);
    };
    element.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      element.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const messageCount = Array.isArray(messages) ? messages.length : 0;
  const lastMessageIsMine = Boolean(
    Array.isArray(messages) && messageCount > 0
      ? messages[messageCount - 1]?.isMe
      : false
  );

  useEffect(() => {
    const element = scrollerRef.current;
    if (!element) return;
    // The user sending their own message should always scroll them to it,
    // even if they had previously scrolled up. Otherwise honor the user's
    // scroll position so we don't yank them away from older history.
    if (!wasNearBottomRef.current && !lastMessageIsMine) return;
    element.scrollTop = element.scrollHeight;
  }, [messageCount, lastMessageIsMine]);

  return scrollerRef;
}

// Coordinates chat route selection, color state, and message read markers for the chat screen.
export function useChatData() {
  const navigate = useNavigate();
  const location = useLocation();
  const isPastChat = location.pathname.startsWith("/past/chat/");
  const isFullScreenChat = useMemo(
    () => location.pathname.startsWith("/active/chat/") || isPastChat,
    [isPastChat, location.pathname]
  );

  const activeContext = useActive();
  const pastContext = useContext(PastContext);
  const context = isPastChat ? pastContext : activeContext;

  const {
    chatListLoading,
    availableChats,
    acceptingChatRequestIds,
    selectedChatPk,
    setSelectedChatPk,
    currentChat,
    messagesLoading,
    messages,
    input,
    setInput,
    sendMessage,
    messageModerationWarning,
    clearMessageModerationWarning,
    me,
    acceptChatRequest,
    denyChatRequest,
    deleteChatRequest,
    hideConversation,
    hideMessage,
  } = context || {};

  const { headerName, pastChat } = useEnsureSelectedChat();
  const [myProfile, setMyProfile] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadMyProfile() {
      const myUserId = String(me?.userId || "").trim();
      if (!myUserId) {
        setMyProfile(null);
        return;
      }

      try {
        const summaries = await getProfileSummaries([myUserId]);
        const mySummary = summaries.get(myUserId) || null;
        if (!cancelled) {
          setMyProfile(mySummary?.profile || null);
        }
      } catch (error) {
        console.error("Failed to load my profile summary for chat color:", error);
        if (!cancelled) {
          setMyProfile(null);
        }
      }
    }

    loadMyProfile();
    return () => {
      cancelled = true;
    };
  }, [me?.userId]);

  const myChatColor = useMemo(() => pickChatColor(myProfile), [myProfile]);
  const counterpartChatColor = useMemo(
    () => pickChatColor(currentChat?.counterpartProfile || currentChat),
    [currentChat]
  );
  const chatAccentColor = myChatColor;
  const hasAvailableChats = Array.isArray(availableChats) && availableChats.length > 0;
  const chatReady = !Boolean(chatListLoading) && (!hasAvailableChats || Boolean(currentChat));
  const scrollerRef = useAutoScrollToBottom(messages);

  useEffect(() => {
    const userId = me?.userId;
    if (!userId || !selectedChatPk || !Array.isArray(messages) || messages.length === 0) return;

    const latestIncomingMessageSk = getLatestIncomingSkFromMessages(messages, userId);
    if (!latestIncomingMessageSk) return;

    let cancelled = false;

    void (async () => {
      const didPersistRead = await persistConversationReadMarker({
        userId,
        chatPk: selectedChatPk,
        lastViewedMessageSk: latestIncomingMessageSk,
        viewedAtIso: new Date().toISOString(),
      });

      if (!cancelled && didPersistRead) {
        invalidateCommunicationInboxSnapshot(userId);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [me?.userId, messages, selectedChatPk]);

  const onBack = () => {
    navigate(isPastChat ? "/past" : "/active");
  };

  const [isSendingMessage, setIsSendingMessage] = useState(false);
  // Synchronous ref guard prevents two events in the same tick (e.g. Enter
  // keydown + button click arriving before the state update propagates) from
  // both reading stale `isSendingMessage === false` and firing two sends.
  const isSendingRef = useRef(false);

  const onSend = async () => {
    if (isSendingRef.current) return;
    isSendingRef.current = true;
    setIsSendingMessage(true);
    try {
      await sendMessage();
    } finally {
      isSendingRef.current = false;
      setIsSendingMessage(false);
    }
  };

  const onKeyDown = (event) => {
    if (event.key !== "Enter") return;
    // Shift+Enter inserts a newline (default textarea behavior); plain Enter
    // sends. Cmd/Ctrl+Enter is also accepted for users used to that pattern.
    if (event.shiftKey) return;
    event.preventDefault();
    if (isSendingRef.current) return;
    onSend();
  };

  return {
    isFullScreenChat,
    chatListLoading,
    chatReady,
    hasAvailableChats,
    headerName,
    availableChats,
    acceptingChatRequestIds,
    currentChat,
    selectedChatPk,
    setSelectedChatPk,
    messagesLoading,
    messages,
    input,
    setInput,
    messageModerationWarning,
    clearMessageModerationWarning,
    onSend,
    onKeyDown,
    isSendingMessage,
    onBack,
    me,
    acceptChatRequest,
    denyChatRequest,
    deleteChatRequest,
    hideConversation,
    hideMessage,
    isPastChat,
    pastChat,
    scrollerRef,
    formatMessageTime,
    chatAccentColor,
    myChatColor,
    counterpartChatColor,
  };
}
